// The DAY a payment was made, as a person picks it — the screen half of
// server/utils/paidDate.js.
//
// `paid_at` decides which month and which YEAR money counts in: Money in &
// out, Sales tax on cash basis and the 1099 totals all read it in the
// company's timezone. The owner, 2026-09-27: "We should be able to change the
// date of paid, in case we forget to mark an invoice paid and then it ends up
// on next year's report."
//
// Every day here is the COMPANY's calendar day, never the viewer's: an
// accountant opening this from another state must not be offered "today" as a
// day that has not started in Connecticut. The server is the authority and
// applies the same rules (paidDate.test.js); this only lets a sheet say so
// before the request rather than after it.
//
// Nothing here moves an amount.
import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { FwField, FwInput } from '../../../ui/fw';
import { getCompanyDateConfig } from '../../../utils/weekUtils';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The company's timezone, from the settings object useSettings() hands back. */
export function companyZone(settings) {
  return getCompanyDateConfig(settings).timezone;
}

/** Today, in the company's timezone, as YYYY-MM-DD. */
export function companyToday(settings, now = new Date()) {
  return formatInTimeZone(now, companyZone(settings), 'yyyy-MM-dd');
}

/**
 * The company-calendar day of a stored `paid_at` ('2025-12-31 17:00:00', UTC,
 * or an ISO string). null when there is none or it cannot be read.
 */
export function paidDayOf(paidAt, timezone) {
  if (!paidAt) return null;
  let s = String(paidAt);
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = `${s.replace(' ', 'T')}Z`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return formatInTimeZone(d, timezone, 'yyyy-MM-dd');
}

/**
 * What is wrong with a picked day, in the words the server would use, or null.
 * The same three rules as readPaidDay: a real date, not in the future, not
 * before 2000.
 */
export function paidDayProblem(day, today) {
  const v = String(day == null ? '' : day).trim();
  if (!DAY.test(v)) return 'Pick the day it was paid.';
  const [y, m, d] = v.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return 'Pick the day it was paid.';
  if (today && v > today) return 'The paid date cannot be in the future.';
  if (v < '2000-01-01') return 'The paid date is before 2000 — check the year.';
  return null;
}

/**
 * The request body's date part. Nothing when the day is TODAY: the server then
 * stamps the moment it is marked, exactly as it did before a day could be
 * picked, so the common case is unchanged. A day named only when it differs.
 */
export function paidDayBody(day, today) {
  return day && day !== today ? { paid_on: day } : {};
}

/**
 * The "Paid on" field. `max` is the company's today, so the picker cannot
 * offer a future day; the hint says what the date decides, because "why does
 * this ask me a date" is the first question and the answer is the tax year.
 */
export function PaidDayField({ id, value, onChange, today, hint, label = 'Paid on' }) {
  // Said for an EMPTY field too: the button is blocked either way, and a
  // blocked button with no reason is a dead end (third review of #719).
  const problem = paidDayProblem(value, today);
  return (
    <FwField
      label={label}
      htmlFor={id}
      hint={hint || 'Decides which month and year this payment counts in on the reports. If it was paid before today, pick that day.'}
      error={problem}
      required
    >
      <FwInput
        id={id}
        type="date"
        value={value || ''}
        max={today || undefined}
        min="2000-01-01"
        onChange={(e) => onChange(e.target.value)}
        data-field="paid-on"
      />
    </FwField>
  );
}
